import React, { ReactNode, useEffect, useRef, useState } from "react";

interface Tab {
  label: string;
  component: ReactNode;
}

interface SwitcherProps {
  tabs: Tab[];
  activeTabIndex?: number;
  onTabChange?: (index: number) => void;
}

const Switcher: React.FC<SwitcherProps> = ({
  tabs,
  activeTabIndex: controlledIndex,
  onTabChange,
}) => {
  const isControlled = controlledIndex !== undefined;

  const [internalIndex, setInternalIndex] = useState<number>(0);

  const activeTabIndex = isControlled ? controlledIndex! : internalIndex;

  const underlineRef = useRef<HTMLDivElement>(null);

  const changeTab = (newIndex: number) => {
    if (newIndex === activeTabIndex) return;

    if (!isControlled) {
      setInternalIndex(newIndex);
    }

    if (onTabChange) {
      onTabChange(newIndex);
    }
  };

  const handleTabClick = (index: number) => {
    changeTab(index);
  };

  // Keep underline in sync
  useEffect(() => {
    if (underlineRef.current) {
      underlineRef.current.style.transform = `translateX(${
        activeTabIndex * 100
      }%)`;
    }
  }, [activeTabIndex, tabs.length]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex relative py-3 bg-gradient-radial-tab flex-shrink-0">
        <div className="flex flex-1">
          {tabs.map((tab, index) => (
            <div key={tab.label} className="flex-1 flex justify-center">
              <span
                className={`cursor-pointer text-[12px] font-medium transition-colors duration-300 ${
                  activeTabIndex === index
                    ? "text-text-tab-active"
                    : "text-text-subtle"
                }`}
                onClick={() => handleTabClick(index)}
                aria-selected={activeTabIndex === index}
                role="tab"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleTabClick(index);
                  }
                }}
              >
                {tab.label.toLocaleUpperCase()}
              </span>
            </div>
          ))}
        </div>

        {/* Base Gray Underline */}
        <div className="absolute bottom-0 left-0 h-[1px] w-full bg-[#ECECEC]"></div>

        {/* Sliding Blue Underline */}
        <div
          ref={underlineRef}
          className="absolute bottom-0 h-[2px] bg-border-tab-active transition-transform duration-300 ease-in-out"
          style={{
            width: `${100 / tabs.length}%`,
            transform: `translateX(${activeTabIndex * 100}%)`,
            zIndex: 10,
          }}
        />
      </div>

      <div className="flex-1 bg-bg-page-body overflow-y-auto relative">
        {tabs[activeTabIndex] && tabs[activeTabIndex].component}
      </div>
    </div>
  );
};

export default Switcher;
